import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import { Stack } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InlineAlert } from '../../../../core-components/theme/components/InlineAlert';
import { ACCOUNT_ROUTES } from '../../../../navigation/paths';
import { useQuietBanner } from '../../hooks/logic/useHeldAlerts';
import { useLocale } from '../../hooks/logic/useLocale';
import { ButtonLink } from '../shared/ButtonLink';

import { hasHeld, quietBannerLine } from './quietText';
import type { ShownQuietBanner } from './quietText';

export const HeldAlertsUnknown: React.FC = () => {
  const { t } = useTranslation();
  return (
    <InlineAlert
      severity="warning"
      message={t(
        'closeTheLoop.quietHours.heldUnknown',
        'Couldn’t check what is being held right now. Your quiet hours are still in force.'
      )}
    />
  );
};

const MOON = <NightsStayOutlinedIcon fontSize="small" />;
const actionsSx = { flexWrap: 'wrap' } as const;

const bannerActionsOf = (t: TFunction, verify: boolean, heldTarget: string | null): React.ReactElement | undefined => {
  if (!verify && heldTarget === null) return undefined;
  return (
    <Stack direction="row" spacing={2} useFlexGap sx={actionsSx}>
      {verify && (
        <ButtonLink intent="tertiary" to={ACCOUNT_ROUTES.contact}>
          {t('closeTheLoop.settings.verifyEmail', 'Verify email')}
        </ButtonLink>
      )}
      {heldTarget !== null && (
        <ButtonLink intent="tertiary" to={heldTarget}>
          {t('closeTheLoop.quietHours.seeHeld', 'See held alerts')}
        </ButtonLink>
      )}
    </Stack>
  );
};

interface QuietHoursBannerProps {
  readonly banner: ShownQuietBanner;
  readonly to?: string;
  readonly sx?: SxProps<Theme>;
}

export const QuietHoursBanner: React.FC<QuietHoursBannerProps> = ({ banner, to, sx }) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const heldTarget = to !== undefined && hasHeld(banner.counts) ? to : null;
  const verify = banner.kind === 'noEmail';
  return (
    <InlineAlert
      role="status"
      severity={verify ? 'warning' : 'info'}
      icon={MOON}
      sx={sx}
      message={quietBannerLine(t, banner, locale)}
      action={bannerActionsOf(t, verify, heldTarget)}
    />
  );
};

export interface HeldAlertsBannerProps {
  readonly to?: string;
  readonly sx?: SxProps<Theme>;
}

export const HeldAlertsBanner: React.FC<HeldAlertsBannerProps> = ({ to, sx }) => {
  const banner = useQuietBanner();
  if (banner.kind === 'hidden') return null;
  return <QuietHoursBanner banner={banner} to={to} sx={sx} />;
};
